/**
 * Kundali form copy for the in-language regional editions (`/bn|gu|mr|or|ta/free-kundali/`).
 *
 * Kept apart from `KundaliFormCard.i18n.ts` on purpose: that file is the Hindi
 * value surface and `scripts/check_hindi_register.py` keeps it Roman Hinglish
 * (rule 21). Marathi is Devanagari by script, not by register, and these
 * editions are read in their own scripts, so they live here.
 *
 * Only what the seeker reads is localized. The language sent to the kundali
 * API, analytics and chat stays `en` (see `KundaliFormCard`'s `displayLocale`),
 * so a regional code never reaches an en/hi contract.
 */
import type { KundaliRegionalLocale } from "@/i18n/regional-locales";

import type { KundaliTranslations } from "./KundaliFormCard.i18n.types";

export type RegionalValidationKey =
  | "nameRequired"
  | "dateFormat"
  | "dateInvalid"
  | "dateTooEarly"
  | "dateFuture"
  | "timeRequired"
  | "placeRequired"
  | "placeNoMatch"
  | "placeUnconfirmed"
  | "phoneInvalid"
  | "emailInvalid";

export type RegionalSubmitErrorKey = "chartIncomplete" | "invalidPhone" | "checkForm" | "generic";

export interface RegionalKundaliFormCopy {
  fields: KundaliTranslations;
  shell: { title: string; body: string; eyebrow: string; openForm: string; activation: string };
  hints: {
    name: string;
    birthDate: string;
    dateFormat: string;
    birthTime: string;
    birthPlace: string;
    placeDefault: string;
    placeNoMatch: string;
  };
  calculation: {
    summary: string;
    coordinates: string;
    timezone: string;
    method: string;
    methodValue: string;
  };
  validation: Record<RegionalValidationKey, string>;
  submitErrors: Record<RegionalSubmitErrorKey, string>;
  /** Same three time-based reassurances as `LOADING_STATUS_MESSAGES`. */
  loadingSteps: readonly [string, string, string];
}

export const REGIONAL_KUNDALI_FORM_COPY: Record<KundaliRegionalLocale, RegionalKundaliFormCopy> = {
  bn: {
    fields: {
      name: "আপনার নাম",
      birthDate: "জন্ম তারিখ",
      birthTime: "জন্ম সময়",
      birthPlace: "জন্মস্থান",
      optional: "(ঐচ্ছিক)",
      timeUnknownToggle: "জন্ম সময় জানা নেই?",
      timeUnknownExplainer:
        "অসুবিধা নেই — জন্ম তারিখ ও স্থান থেকে আমরা একটি সংক্ষিপ্ত, সময়-নিরপেক্ষ বিশ্লেষণ দেখাব। সঠিক জন্ম সময় ছাড়া আমরা নির্দিষ্ট লগ্ন, ভাব, বর্গ চক্র বা দশার সময় দাবি করি না।",
      submit: "ফ্রি কুষ্ঠি তৈরি করুন →",
      namePlaceholder: "আপনার পুরো নাম লিখুন",
      placePlaceholder: "শহর, রাজ্য (যেমন Kolkata, West Bengal)",
      savedNote: "আপনার কুষ্ঠি নিরাপদে সংরক্ষিত থাকে, যেকোনো সময় দেখতে পারেন।",
      loading: "আপনার গ্রহ গণনা করা হচ্ছে...",
      phone: "WhatsApp মোবাইল নম্বর",
      phonePlaceholder: "98765 43210",
      phoneCountryLabel: "দেশের কোড",
      phoneReason:
        "ঐচ্ছিক। কুষ্ঠি সংরক্ষণ করে পরে আবার খুলতে নম্বরটি দিন। এতে আপনি মার্কেটিং বার্তার জন্য সম্মতি দিচ্ছেন না।",
      email: "ইমেল",
      emailPlaceholder: "you@example.com (ঐচ্ছিক)",
      emailToggle: "ব্যাকআপ হিসেবে ইমেল যোগ করুন (ঐচ্ছিক)",
      placeChange: "বদলান",
      placeDidYouMean: "আপনি কি বোঝাতে চেয়েছেন",
      retryTitle: "এই মুহূর্তে গণনা সম্পূর্ণ হলো না",
      retryBody:
        "আপনার তথ্য সংরক্ষিত আছে। সংযোগ বিচ্ছিন্ন হয়েছে — আরেকবার চেষ্টা করতে ট্যাপ করুন।",
      retryAction: "আবার চেষ্টা করুন",
    },
    shell: {
      title: "আপনার ফ্রি জন্ম কুষ্ঠি তৈরি করুন",
      body: "জন্মের তথ্য দিন, আর বাংলায় আপনার কুষ্ঠি ছক, গ্রহের অবস্থান ও দশা দেখুন।",
      eyebrow: "ফ্রি বৈদিক কুষ্ঠি",
      openForm: "ফর্ম খুলুন",
      activation: "জন্মের তথ্য দিন",
    },
    hints: {
      name: "আপনার নাম বিশ্লেষণটিকে ব্যক্তিগত করে তোলে",
      birthDate: "সঠিক জন্ম তারিখে গ্রহের সঠিক অবস্থান জানা যায়",
      dateFormat: "dd/mm/yyyy আকারে লিখুন, যেমন 15/08/1992।",
      birthTime: "জন্ম সময় থেকে আপনার লগ্ন ও দশার সময় নির্ধারিত হয়",
      birthPlace: "জন্মস্থান থেকে আপনার কুষ্ঠির সঠিক অক্ষাংশ-দ্রাঘিমাংশ ও সময় অঞ্চল পাওয়া যায়",
      placeDefault:
        "শহরের নাম ইংরেজি অক্ষরে লিখে জমা দিন — সঠিক নাম নিজে থেকেই নির্বাচিত হবে। চাইলে কোনো প্রস্তাবে ট্যাপ করুন।",
      placeNoMatch:
        "এই বানানে কোনো শহর পাওয়া যায়নি — আপনার জেলা বা কাছের বড় শহরের নাম ইংরেজি অক্ষরে লিখুন।",
    },
    calculation: {
      summary: "গণনার বিবরণ",
      coordinates: "অক্ষাংশ-দ্রাঘিমাংশ",
      timezone: "সময় অঞ্চল",
      method: "কুষ্ঠি পদ্ধতি",
      methodValue: "লাহিড়ী অয়নাংশ · সম্পূর্ণ রাশি ভাব",
    },
    validation: {
      nameRequired: "অনুগ্রহ করে আপনার নাম লিখুন।",
      dateFormat: "জন্ম তারিখ dd/mm/yyyy আকারে লিখুন।",
      dateInvalid: "এই তারিখটি সঠিক নয় — দিন ও মাস দেখে নিন।",
      dateTooEarly: "জন্ম সাল 1900 বা তার পরে হতে হবে।",
      dateFuture: "জন্ম তারিখ ভবিষ্যতের হতে পারে না।",
      timeRequired: "চ্যাট শুরু করতে জন্ম সময় দিন।",
      placeRequired: "অনুগ্রহ করে আপনার জন্মস্থান লিখুন।",
      placeNoMatch:
        "এই শহরটি আমাদের তালিকায় পাওয়া যায়নি — আপনার জেলা বা কাছের বড় শহরের নাম ইংরেজি বানানে লিখুন।",
      placeUnconfirmed: "শহরটি নিশ্চিত করা গেল না — বানান দেখে নিন বা কোনো প্রস্তাবে ট্যাপ করুন।",
      phoneInvalid: "বেছে নেওয়া দেশের জন্য সঠিক মোবাইল নম্বর দিন, অথবা ফাঁকা রাখুন।",
      emailInvalid: "সঠিক ইমেল দিন, অথবা ফাঁকা রাখুন।",
    },
    submitErrors: {
      chartIncomplete: "আপনার সম্পূর্ণ কুষ্ঠি এখন তৈরি করা গেল না। অনুগ্রহ করে আবার চেষ্টা করুন।",
      invalidPhone: "সঠিক মোবাইল নম্বর দিন।",
      checkForm: "ফর্মের তথ্য একবার দেখে নিন।",
      generic: "কিছু ভুল হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।",
    },
    loadingSteps: [
      "আপনার কুষ্ঠি তৈরি হচ্ছে। সাধারণত কয়েক সেকেন্ড লাগে।",
      "আপনার গ্রহের অবস্থান গণনা করা হচ্ছে। অনুগ্রহ করে পেজটি খোলা রাখুন।",
      "বিস্তারিত গণনা এখনও চলছে। কিছু কুষ্ঠিতে একটু বেশি সময় লাগে।",
    ],
  },
  gu: {
    fields: {
      name: "તમારું નામ",
      birthDate: "જન્મ તારીખ",
      birthTime: "જન્મ સમય",
      birthPlace: "જન્મ સ્થળ",
      optional: "(વૈકલ્પિક)",
      timeUnknownToggle: "જન્મ સમય ખબર નથી?",
      timeUnknownExplainer:
        "કોઈ વાંધો નહીં — જન્મ તારીખ અને સ્થળ પરથી અમે ટૂંકું, સમય પર આધાર ન રાખતું વિશ્લેષણ બતાવીશું. ચોક્કસ જન્મ સમય વિના અમે ચોક્કસ લગ્ન, ભાવ, વર્ગ કુંડળી કે દશાનો સમય દાવો કરતા નથી.",
      submit: "ફ્રી કુંડળી બનાવો →",
      namePlaceholder: "તમારું પૂરું નામ લખો",
      placePlaceholder: "શહેર, રાજ્ય (દા.ત. Ahmedabad, Gujarat)",
      savedNote: "તમારી કુંડળી સુરક્ષિત રીતે સચવાય છે, ગમે ત્યારે જોઈ શકો છો.",
      loading: "તમારા ગ્રહોની ગણતરી થઈ રહી છે...",
      phone: "WhatsApp મોબાઇલ નંબર",
      phonePlaceholder: "98765 43210",
      phoneCountryLabel: "દેશ કોડ",
      phoneReason:
        "વૈકલ્પિક. કુંડળી સાચવીને પછી ફરી ખોલવા માટે નંબર ઉમેરો. તેનાથી તમે માર્કેટિંગ સંદેશા માટે સંમતિ આપતા નથી.",
      email: "ઇમેઇલ",
      emailPlaceholder: "you@example.com (વૈકલ્પિક)",
      emailToggle: "બેકઅપ તરીકે ઇમેઇલ ઉમેરો (વૈકલ્પિક)",
      placeChange: "બદલો",
      placeDidYouMean: "શું તમારો મતલબ હતો",
      retryTitle: "અત્યારે ગણતરી પૂરી ન થઈ શકી",
      retryBody: "તમારી વિગતો સચવાયેલી છે. જોડાણ તૂટી ગયું — ફરી પ્રયાસ કરવા ટેપ કરો.",
      retryAction: "ફરી પ્રયાસ કરો",
    },
    shell: {
      title: "તમારી ફ્રી જન્મ કુંડળી બનાવો",
      body: "જન્મની વિગતો આપો અને ગુજરાતીમાં તમારી કુંડળી, ગ્રહ સ્થિતિ અને દશા જુઓ.",
      eyebrow: "ફ્રી વૈદિક કુંડળી",
      openForm: "ફોર્મ ખોલો",
      activation: "જન્મની વિગતો ભરો",
    },
    hints: {
      name: "તમારું નામ વિશ્લેષણને વ્યક્તિગત બનાવે છે",
      birthDate: "સાચી જન્મ તારીખથી ગ્રહોની સાચી સ્થિતિ મળે છે",
      dateFormat: "dd/mm/yyyy રીતે લખો, દા.ત. 15/08/1992.",
      birthTime: "જન્મ સમયથી તમારું લગ્ન અને દશાનો સમય નક્કી થાય છે",
      birthPlace: "જન્મ સ્થળથી તમારી કુંડળી માટે ચોક્કસ અક્ષાંશ-રેખાંશ અને ટાઇમઝોન મળે છે",
      placeDefault:
        "શહેરનું નામ અંગ્રેજી અક્ષરોમાં લખીને સબમિટ કરો — સાચું નામ આપમેળે પસંદ થશે. ઇચ્છો તો કોઈ સૂચન પર ટેપ કરો.",
      placeNoMatch:
        "આ જોડણીથી કોઈ શહેર મળ્યું નથી — તમારો જિલ્લો કે નજીકનું મોટું શહેર અંગ્રેજી અક્ષરોમાં લખો.",
    },
    calculation: {
      summary: "ગણતરીની વિગતો",
      coordinates: "અક્ષાંશ-રેખાંશ",
      timezone: "ટાઇમઝોન",
      method: "કુંડળી પદ્ધતિ",
      methodValue: "લાહિરી અયનાંશ · સંપૂર્ણ રાશિ ભાવ",
    },
    validation: {
      nameRequired: "કૃપા કરીને તમારું નામ લખો.",
      dateFormat: "જન્મ તારીખ dd/mm/yyyy રીતે લખો.",
      dateInvalid: "આ તારીખ સાચી નથી — દિવસ અને મહિનો તપાસો.",
      dateTooEarly: "જન્મ વર્ષ 1900 કે તે પછીનું હોવું જોઈએ.",
      dateFuture: "જન્મ તારીખ ભવિષ્યની ન હોઈ શકે.",
      timeRequired: "ચેટ શરૂ કરવા માટે જન્મ સમય આપો.",
      placeRequired: "કૃપા કરીને તમારું જન્મ સ્થળ લખો.",
      placeNoMatch:
        "આ શહેર અમારી યાદીમાં મળ્યું નથી — તમારો જિલ્લો કે નજીકનું મોટું શહેર અંગ્રેજી જોડણીમાં લખો.",
      placeUnconfirmed: "આ શહેરની પુષ્ટિ થઈ શકી નથી — જોડણી તપાસો અથવા કોઈ સૂચન પર ટેપ કરો.",
      phoneInvalid: "પસંદ કરેલા દેશ માટે સાચો મોબાઇલ નંબર આપો, અથવા ખાલી રાખો.",
      emailInvalid: "સાચો ઇમેઇલ આપો, અથવા ખાલી રાખો.",
    },
    submitErrors: {
      chartIncomplete: "તમારી પૂરી કુંડળી અત્યારે તૈયાર થઈ શકી નથી. કૃપા કરીને ફરી પ્રયાસ કરો.",
      invalidPhone: "સાચો મોબાઇલ નંબર લખો.",
      checkForm: "ફોર્મની વિગતો તપાસો.",
      generic: "કંઈક ખોટું થયું. કૃપા કરીને ફરી પ્રયાસ કરો.",
    },
    loadingSteps: [
      "તમારી કુંડળી તૈયાર થઈ રહી છે. સામાન્ય રીતે થોડી જ સેકન્ડ લાગે છે.",
      "તમારા ગ્રહોની ગણતરી થઈ રહી છે. કૃપા કરીને આ પેજ ખુલ્લું રાખો.",
      "વિગતોની ગણતરી હજુ ચાલુ છે. કેટલીક કુંડળીમાં થોડો વધુ સમય લાગે છે.",
    ],
  },
  mr: {
    fields: {
      name: "तुमचे नाव",
      birthDate: "जन्मतारीख",
      birthTime: "जन्मवेळ",
      birthPlace: "जन्मस्थान",
      optional: "(ऐच्छिक)",
      timeUnknownToggle: "जन्मवेळ माहीत नाही?",
      timeUnknownExplainer:
        "काही हरकत नाही — जन्मतारीख आणि जन्मस्थानावरून आम्ही वेळेवर अवलंबून नसलेले मर्यादित विश्लेषण दाखवू. अचूक जन्मवेळेशिवाय आम्ही नेमके लग्न, भाव, वर्ग कुंडली किंवा दशेचा काळ सांगत नाही.",
      submit: "मोफत कुंडली बनवा →",
      namePlaceholder: "तुमचे पूर्ण नाव लिहा",
      placePlaceholder: "शहर, राज्य (उदा. Pune, Maharashtra)",
      savedNote: "तुमची कुंडली सुरक्षितपणे जतन होते, ती कधीही पाहता येते.",
      loading: "तुमच्या ग्रहांची गणना होत आहे...",
      phone: "WhatsApp मोबाईल नंबर",
      phonePlaceholder: "98765 43210",
      phoneCountryLabel: "देश कोड",
      phoneReason:
        "ऐच्छिक. कुंडली जतन करून नंतर पुन्हा उघडण्यासाठी नंबर द्या. यामुळे तुम्ही मार्केटिंग संदेशांना संमती देत नाही.",
      email: "ईमेल",
      emailPlaceholder: "you@example.com (ऐच्छिक)",
      emailToggle: "बॅकअपसाठी ईमेल जोडा (ऐच्छिक)",
      placeChange: "बदला",
      placeDidYouMean: "तुम्हाला हे म्हणायचे होते का",
      retryTitle: "आत्ता गणना पूर्ण झाली नाही",
      retryBody: "तुमची माहिती जतन आहे. कनेक्शन तुटले — पुन्हा प्रयत्न करण्यासाठी टॅप करा.",
      retryAction: "पुन्हा प्रयत्न करा",
    },
    shell: {
      title: "तुमची मोफत जन्म कुंडली बनवा",
      body: "जन्माची माहिती द्या आणि मराठीत तुमची कुंडली, ग्रहस्थिती आणि दशा पाहा.",
      eyebrow: "मोफत वैदिक कुंडली",
      openForm: "फॉर्म उघडा",
      activation: "जन्माची माहिती भरा",
    },
    hints: {
      name: "तुमच्या नावामुळे विश्लेषण वैयक्तिक होते",
      birthDate: "अचूक जन्मतारखेमुळे ग्रहांची स्थिती अचूक कळते",
      dateFormat: "dd/mm/yyyy या स्वरूपात लिहा, उदा. 15/08/1992.",
      birthTime: "जन्मवेळेवरून तुमचे लग्न आणि दशेचा काळ ठरतो",
      birthPlace: "जन्मस्थानावरून तुमच्या कुंडलीसाठी अचूक अक्षांश-रेखांश आणि टाइमझोन मिळतो",
      placeDefault:
        "शहराचे नाव इंग्रजी अक्षरांत लिहून सबमिट करा — अचूक नाव आपोआप निवडले जाईल. हवे असल्यास एखाद्या सूचनेवर टॅप करा.",
      placeNoMatch:
        "या स्पेलिंगने कोणतेही शहर सापडले नाही — तुमचा जिल्हा किंवा जवळचे मोठे शहर इंग्रजी अक्षरांत लिहा.",
    },
    calculation: {
      summary: "गणनेचा तपशील",
      coordinates: "अक्षांश-रेखांश",
      timezone: "टाइमझोन",
      method: "कुंडली पद्धत",
      methodValue: "लाहिरी अयनांश · संपूर्ण राशी भाव",
    },
    validation: {
      nameRequired: "कृपया तुमचे नाव लिहा.",
      dateFormat: "जन्मतारीख dd/mm/yyyy या स्वरूपात लिहा.",
      dateInvalid: "ही तारीख योग्य नाही — दिवस आणि महिना तपासा.",
      dateTooEarly: "जन्मवर्ष 1900 किंवा त्यानंतरचे असावे.",
      dateFuture: "जन्मतारीख भविष्यातील असू शकत नाही.",
      timeRequired: "चॅट सुरू करण्यासाठी जन्मवेळ द्या.",
      placeRequired: "कृपया तुमचे जन्मस्थान लिहा.",
      placeNoMatch:
        "हे शहर आमच्या यादीत सापडले नाही — तुमचा जिल्हा किंवा जवळचे मोठे शहर इंग्रजी स्पेलिंगमध्ये लिहा.",
      placeUnconfirmed:
        "हे शहर निश्चित करता आले नाही — स्पेलिंग तपासा किंवा एखाद्या सूचनेवर टॅप करा.",
      phoneInvalid: "निवडलेल्या देशासाठी योग्य मोबाईल नंबर द्या, किंवा रिकामे ठेवा.",
      emailInvalid: "योग्य ईमेल द्या, किंवा रिकामे ठेवा.",
    },
    submitErrors: {
      chartIncomplete: "तुमची पूर्ण कुंडली आत्ता तयार होऊ शकली नाही. कृपया पुन्हा प्रयत्न करा.",
      invalidPhone: "योग्य मोबाईल नंबर लिहा.",
      checkForm: "फॉर्ममधील माहिती तपासा.",
      generic: "काहीतरी चुकले. कृपया पुन्हा प्रयत्न करा.",
    },
    loadingSteps: [
      "तुमची कुंडली तयार होत आहे. साधारणपणे काही सेकंद लागतात.",
      "तुमच्या ग्रहांची गणना होत आहे. कृपया हे पान उघडे ठेवा.",
      "तपशिलांची गणना अजून सुरू आहे. काही कुंडल्यांना थोडा जास्त वेळ लागतो.",
    ],
  },
  or: {
    fields: {
      name: "ଆପଣଙ୍କ ନାମ",
      birthDate: "ଜନ୍ମ ତାରିଖ",
      birthTime: "ଜନ୍ମ ସମୟ",
      birthPlace: "ଜନ୍ମ ସ୍ଥାନ",
      optional: "(ଇଚ୍ଛାଧୀନ)",
      timeUnknownToggle: "ଜନ୍ମ ସମୟ ଜଣା ନାହିଁ?",
      timeUnknownExplainer:
        "କିଛି ଅସୁବିଧା ନାହିଁ — ଜନ୍ମ ତାରିଖ ଓ ସ୍ଥାନରୁ ଆମେ ସମୟ ଉପରେ ନିର୍ଭର କରୁନଥିବା ସଂକ୍ଷିପ୍ତ ବିଶ୍ଳେଷଣ ଦେଖାଇବୁ। ସଠିକ୍ ଜନ୍ମ ସମୟ ବିନା ଆମେ ନିର୍ଦ୍ଦିଷ୍ଟ ଲଗ୍ନ, ଭାବ, ବର୍ଗ କୁଣ୍ଡଳୀ ବା ଦଶାର ସମୟ ଦାବି କରୁନାହୁଁ।",
      submit: "ମାଗଣା କୁଣ୍ଡଳୀ ତିଆରି କରନ୍ତୁ →",
      namePlaceholder: "ଆପଣଙ୍କ ପୂରା ନାମ ଲେଖନ୍ତୁ",
      placePlaceholder: "ସହର, ରାଜ୍ୟ (ଯେପରି Bhubaneswar, Odisha)",
      savedNote: "ଆପଣଙ୍କ କୁଣ୍ଡଳୀ ସୁରକ୍ଷିତ ଭାବେ ସଂରକ୍ଷିତ ହୁଏ, ଯେକୌଣସି ସମୟରେ ଦେଖିପାରିବେ।",
      loading: "ଆପଣଙ୍କ ଗ୍ରହ ଗଣନା କରାଯାଉଛି...",
      phone: "WhatsApp ମୋବାଇଲ୍ ନମ୍ବର",
      phonePlaceholder: "98765 43210",
      phoneCountryLabel: "ଦେଶ କୋଡ୍",
      phoneReason:
        "ଇଚ୍ଛାଧୀନ। କୁଣ୍ଡଳୀ ସଂରକ୍ଷଣ କରି ପରେ ପୁଣି ଖୋଲିବା ପାଇଁ ନମ୍ବର ଦିଅନ୍ତୁ। ଏହା ଦ୍ୱାରା ଆପଣ ମାର୍କେଟିଂ ବାର୍ତ୍ତା ପାଇଁ ସମ୍ମତି ଦେଉନାହାନ୍ତି।",
      email: "ଇମେଲ୍",
      emailPlaceholder: "you@example.com (ଇଚ୍ଛାଧୀନ)",
      emailToggle: "ବ୍ୟାକଅପ୍ ପାଇଁ ଇମେଲ୍ ଯୋଡନ୍ତୁ (ଇଚ୍ଛାଧୀନ)",
      placeChange: "ବଦଳାନ୍ତୁ",
      placeDidYouMean: "ଆପଣ କ'ଣ ଏହା କହିବାକୁ ଚାହୁଁଥିଲେ",
      retryTitle: "ବର୍ତ୍ତମାନ ଗଣନା ସମ୍ପୂର୍ଣ୍ଣ ହୋଇପାରିଲା ନାହିଁ",
      retryBody:
        "ଆପଣଙ୍କ ତଥ୍ୟ ସଂରକ୍ଷିତ ଅଛି। ସଂଯୋଗ ବିଚ୍ଛିନ୍ନ ହେଲା — ପୁଣି ଚେଷ୍ଟା କରିବାକୁ ଟାପ୍ କରନ୍ତୁ।",
      retryAction: "ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ",
    },
    shell: {
      title: "ଆପଣଙ୍କ ମାଗଣା ଜନ୍ମ କୁଣ୍ଡଳୀ ତିଆରି କରନ୍ତୁ",
      body: "ଜନ୍ମ ତଥ୍ୟ ଦିଅନ୍ତୁ ଏବଂ ଓଡ଼ିଆରେ ଆପଣଙ୍କ କୁଣ୍ଡଳୀ, ଗ୍ରହ ସ୍ଥିତି ଓ ଦଶା ଦେଖନ୍ତୁ।",
      eyebrow: "ମାଗଣା ବୈଦିକ କୁଣ୍ଡଳୀ",
      openForm: "ଫର୍ମ ଖୋଲନ୍ତୁ",
      activation: "ଜନ୍ମ ତଥ୍ୟ ଦିଅନ୍ତୁ",
    },
    hints: {
      name: "ଆପଣଙ୍କ ନାମ ବିଶ୍ଳେଷଣକୁ ବ୍ୟକ୍ତିଗତ କରେ",
      birthDate: "ସଠିକ୍ ଜନ୍ମ ତାରିଖରୁ ଗ୍ରହମାନଙ୍କ ସଠିକ୍ ସ୍ଥିତି ଜଣାପଡ଼େ",
      dateFormat: "dd/mm/yyyy ଆକାରରେ ଲେଖନ୍ତୁ, ଯେପରି 15/08/1992।",
      birthTime: "ଜନ୍ମ ସମୟରୁ ଆପଣଙ୍କ ଲଗ୍ନ ଓ ଦଶାର ସମୟ ସ୍ଥିର ହୁଏ",
      birthPlace: "ଜନ୍ମ ସ୍ଥାନରୁ ଆପଣଙ୍କ କୁଣ୍ଡଳୀ ପାଇଁ ସଠିକ୍ ଅକ୍ଷାଂଶ-ଦ୍ରାଘିମା ଓ ଟାଇମଜୋନ୍ ମିଳେ",
      placeDefault:
        "ସହରର ନାମ ଇଂରାଜୀ ଅକ୍ଷରରେ ଲେଖି ଦାଖଲ କରନ୍ତୁ — ସଠିକ୍ ନାମ ଆପେ ବାଛି ହୋଇଯିବ। ଚାହିଁଲେ କୌଣସି ପରାମର୍ଶରେ ଟାପ୍ କରନ୍ତୁ।",
      placeNoMatch:
        "ଏହି ବନାନରେ କୌଣସି ସହର ମିଳିଲା ନାହିଁ — ଆପଣଙ୍କ ଜିଲ୍ଲା ବା ନିକଟସ୍ଥ ବଡ଼ ସହରର ନାମ ଇଂରାଜୀ ଅକ୍ଷରରେ ଲେଖନ୍ତୁ।",
    },
    calculation: {
      summary: "ଗଣନାର ବିବରଣୀ",
      coordinates: "ଅକ୍ଷାଂଶ-ଦ୍ରାଘିମା",
      timezone: "ଟାଇମଜୋନ୍",
      method: "କୁଣ୍ଡଳୀ ପଦ୍ଧତି",
      methodValue: "ଲାହିଡ଼ୀ ଅୟନାଂଶ · ପୂର୍ଣ୍ଣ ରାଶି ଭାବ",
    },
    validation: {
      nameRequired: "ଦୟାକରି ଆପଣଙ୍କ ନାମ ଲେଖନ୍ତୁ।",
      dateFormat: "ଜନ୍ମ ତାରିଖ dd/mm/yyyy ଆକାରରେ ଲେଖନ୍ତୁ।",
      dateInvalid: "ଏହି ତାରିଖ ଠିକ୍ ନୁହେଁ — ଦିନ ଓ ମାସ ଯାଞ୍ଚ କରନ୍ତୁ।",
      dateTooEarly: "ଜନ୍ମ ବର୍ଷ 1900 ବା ତା' ପରେ ହେବା ଆବଶ୍ୟକ।",
      dateFuture: "ଜନ୍ମ ତାରିଖ ଭବିଷ୍ୟତର ହୋଇପାରିବ ନାହିଁ।",
      timeRequired: "ଚାଟ୍ ଆରମ୍ଭ କରିବାକୁ ଜନ୍ମ ସମୟ ଦିଅନ୍ତୁ।",
      placeRequired: "ଦୟାକରି ଆପଣଙ୍କ ଜନ୍ମ ସ୍ଥାନ ଲେଖନ୍ତୁ।",
      placeNoMatch:
        "ଏହି ସହର ଆମ ତାଲିକାରେ ମିଳିଲା ନାହିଁ — ଆପଣଙ୍କ ଜିଲ୍ଲା ବା ନିକଟସ୍ଥ ବଡ଼ ସହରର ନାମ ଇଂରାଜୀ ବନାନରେ ଲେଖନ୍ତୁ।",
      placeUnconfirmed:
        "ଏହି ସହର ନିଶ୍ଚିତ କରିହେଲା ନାହିଁ — ବନାନ ଯାଞ୍ଚ କରନ୍ତୁ ବା କୌଣସି ପରାମର୍ଶରେ ଟାପ୍ କରନ୍ତୁ।",
      phoneInvalid: "ବଛା ଯାଇଥିବା ଦେଶ ପାଇଁ ଠିକ୍ ମୋବାଇଲ୍ ନମ୍ବର ଦିଅନ୍ତୁ, ବା ଖାଲି ରଖନ୍ତୁ।",
      emailInvalid: "ଠିକ୍ ଇମେଲ୍ ଦିଅନ୍ତୁ, ବା ଖାଲି ରଖନ୍ତୁ।",
    },
    submitErrors: {
      chartIncomplete:
        "ଆପଣଙ୍କ ସମ୍ପୂର୍ଣ୍ଣ କୁଣ୍ଡଳୀ ବର୍ତ୍ତମାନ ପ୍ରସ୍ତୁତ ହୋଇପାରିଲା ନାହିଁ। ଦୟାକରି ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।",
      invalidPhone: "ଠିକ୍ ମୋବାଇଲ୍ ନମ୍ବର ଲେଖନ୍ତୁ।",
      checkForm: "ଫର୍ମର ତଥ୍ୟ ଯାଞ୍ଚ କରନ୍ତୁ।",
      generic: "କିଛି ଭୁଲ ହେଲା। ଦୟାକରି ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।",
    },
    loadingSteps: [
      "ଆପଣଙ୍କ କୁଣ୍ଡଳୀ ପ୍ରସ୍ତୁତ ହେଉଛି। ସାଧାରଣତଃ କିଛି ସେକେଣ୍ଡ ଲାଗେ।",
      "ଆପଣଙ୍କ ଗ୍ରହ ସ୍ଥିତି ଗଣନା କରାଯାଉଛି। ଦୟାକରି ଏହି ପେଜ୍ ଖୋଲା ରଖନ୍ତୁ।",
      "ବିସ୍ତୃତ ଗଣନା ଏବେ ବି ଚାଲିଛି। କେତେକ କୁଣ୍ଡଳୀରେ ଟିକେ ଅଧିକ ସମୟ ଲାଗେ।",
    ],
  },
  ta: {
    fields: {
      name: "உங்கள் பெயர்",
      birthDate: "பிறந்த தேதி",
      birthTime: "பிறந்த நேரம்",
      birthPlace: "பிறந்த இடம்",
      optional: "(விருப்பத்தேர்வு)",
      timeUnknownToggle: "பிறந்த நேரம் தெரியவில்லையா?",
      timeUnknownExplainer:
        "பரவாயில்லை — பிறந்த தேதி மற்றும் இடத்தைக் கொண்டு நேரத்தைச் சாராத சுருக்கமான பலனைக் காட்டுவோம். சரியான பிறந்த நேரம் இல்லாமல் துல்லியமான லக்னம், பாவங்கள், வர்க்க சக்கரங்கள் அல்லது தசா காலங்களை நாங்கள் கூறுவதில்லை.",
      submit: "இலவச ஜாதகம் உருவாக்கவும் →",
      namePlaceholder: "உங்கள் முழுப் பெயரை உள்ளிடவும்",
      placePlaceholder: "நகரம், மாநிலம் (எ.கா. Chennai, Tamil Nadu)",
      savedNote: "உங்கள் ஜாதகம் பாதுகாப்பாகச் சேமிக்கப்படும்; எப்போது வேண்டுமானாலும் பார்க்கலாம்.",
      loading: "உங்கள் கிரகங்கள் கணிக்கப்படுகின்றன...",
      phone: "WhatsApp மொபைல் எண்",
      phonePlaceholder: "98765 43210",
      phoneCountryLabel: "நாட்டுக் குறியீடு",
      phoneReason:
        "விருப்பத்தேர்வு. ஜாதகத்தைச் சேமித்து பின்னர் மீண்டும் திறக்க எண்ணைச் சேர்க்கவும். இதனால் நீங்கள் விளம்பரச் செய்திகளுக்கு ஒப்புதல் அளிப்பதில்லை.",
      email: "மின்னஞ்சல்",
      emailPlaceholder: "you@example.com (விருப்பத்தேர்வு)",
      emailToggle: "காப்புக்காக மின்னஞ்சலைச் சேர்க்கவும் (விருப்பத்தேர்வு)",
      placeChange: "மாற்று",
      placeDidYouMean: "நீங்கள் குறிப்பிட்டது இதுவா",
      retryTitle: "இப்போது கணிப்பை முடிக்க முடியவில்லை",
      retryBody:
        "உங்கள் விவரங்கள் சேமிக்கப்பட்டுள்ளன. இணைப்பு துண்டிக்கப்பட்டது — மீண்டும் முயல தட்டவும்.",
      retryAction: "மீண்டும் முயலவும்",
    },
    shell: {
      title: "உங்கள் இலவச ஜாதகத்தை உருவாக்கவும்",
      body: "பிறப்பு விவரங்களைத் தந்து, உங்கள் ஜாதகக் கட்டம், கிரக நிலைகள் மற்றும் தசையைத் தமிழில் பாருங்கள்.",
      eyebrow: "இலவச வேத ஜாதகம்",
      openForm: "படிவத்தைத் திற",
      activation: "பிறப்பு விவரங்களை உள்ளிடவும்",
    },
    hints: {
      name: "உங்கள் பெயர் பலனைத் தனிப்பட்டதாக்குகிறது",
      birthDate: "சரியான பிறந்த தேதியால் கிரக நிலைகள் சரியாகக் கணிக்கப்படும்",
      dateFormat: "dd/mm/yyyy வடிவில் எழுதவும், எ.கா. 15/08/1992.",
      birthTime: "பிறந்த நேரமே உங்கள் லக்னத்தையும் தசா காலத்தையும் தீர்மானிக்கிறது",
      birthPlace:
        "பிறந்த இடத்திலிருந்து உங்கள் ஜாதகத்திற்கான சரியான அட்சரேகை, தீர்க்கரேகை மற்றும் நேர மண்டலம் கிடைக்கும்",
      placeDefault:
        "நகரத்தின் பெயரை ஆங்கில எழுத்துகளில் உள்ளிட்டுச் சமர்ப்பிக்கவும் — சரியான பெயர் தானாகத் தேர்வாகும். விரும்பினால் ஒரு பரிந்துரையைத் தட்டவும்.",
      placeNoMatch:
        "இந்த எழுத்துக்கூட்டலில் எந்த ஊரும் கிடைக்கவில்லை — உங்கள் மாவட்டம் அல்லது அருகிலுள்ள பெரிய நகரத்தை ஆங்கில எழுத்துகளில் உள்ளிடவும்.",
    },
    calculation: {
      summary: "கணிப்பு விவரங்கள்",
      coordinates: "அட்சரேகை, தீர்க்கரேகை",
      timezone: "நேர மண்டலம்",
      method: "ஜாதக முறை",
      methodValue: "லாஹிரி அயனாம்சம் · முழு ராசி பாவங்கள்",
    },
    validation: {
      nameRequired: "உங்கள் பெயரை உள்ளிடவும்.",
      dateFormat: "பிறந்த தேதியை dd/mm/yyyy வடிவில் உள்ளிடவும்.",
      dateInvalid: "இந்தத் தேதி சரியில்லை — நாள் மற்றும் மாதத்தைச் சரிபார்க்கவும்.",
      dateTooEarly: "பிறந்த ஆண்டு 1900 அல்லது அதற்குப் பிறகு இருக்க வேண்டும்.",
      dateFuture: "பிறந்த தேதி எதிர்காலத்தில் இருக்க முடியாது.",
      timeRequired: "அரட்டையைத் தொடங்க பிறந்த நேரத்தை உள்ளிடவும்.",
      placeRequired: "உங்கள் பிறந்த இடத்தை உள்ளிடவும்.",
      placeNoMatch:
        "இந்த ஊர் எங்கள் பட்டியலில் இல்லை — உங்கள் மாவட்டம் அல்லது அருகிலுள்ள பெரிய நகரத்தை ஆங்கில எழுத்துக்கூட்டலில் உள்ளிடவும்.",
      placeUnconfirmed:
        "இந்த நகரத்தை உறுதிப்படுத்த முடியவில்லை — எழுத்துக்கூட்டலைச் சரிபார்க்கவும் அல்லது ஒரு பரிந்துரையைத் தட்டவும்.",
      phoneInvalid:
        "தேர்ந்தெடுத்த நாட்டிற்கான சரியான மொபைல் எண்ணை உள்ளிடவும், அல்லது காலியாக விடவும்.",
      emailInvalid: "சரியான மின்னஞ்சலை உள்ளிடவும், அல்லது காலியாக விடவும்.",
    },
    submitErrors: {
      chartIncomplete: "உங்கள் முழு ஜாதகத்தை இப்போது தயாரிக்க முடியவில்லை. மீண்டும் முயலவும்.",
      invalidPhone: "சரியான மொபைல் எண்ணை உள்ளிடவும்.",
      checkForm: "படிவத்தில் உள்ள விவரங்களைச் சரிபார்க்கவும்.",
      generic: "ஏதோ தவறு நடந்தது. மீண்டும் முயலவும்.",
    },
    loadingSteps: [
      "உங்கள் ஜாதகம் தயாராகிறது. பொதுவாக சில விநாடிகளே ஆகும்.",
      "உங்கள் கிரக நிலைகள் கணிக்கப்படுகின்றன. இந்தப் பக்கத்தைத் திறந்தே வைத்திருங்கள்.",
      "விவரங்களின் கணிப்பு இன்னும் நடக்கிறது. சில ஜாதகங்களுக்குச் சற்று அதிக நேரம் ஆகும்.",
    ],
  },
};
